import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, History, Loader2, Send } from "lucide-react";
import type { ReactNode } from "react";
import { orpc } from "@/frontend/orpc-client";
import { SUGGESTED_SOURCE_TARGET } from "@/server/constants/suggested-sources";
import { Button } from "@/frontend/components/ui/button";
import { InfoHint } from "@/frontend/components/ui/info-hint";
import { Switch } from "@/frontend/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/frontend/components/ui/dialog";
import { cn } from "@/frontend/lib/utils";
import { onboardingAsks } from "@/frontend/types/dashboard/onboarding";
import { daysLeft, formatDateTime } from "@/frontend/utils/shared";
import {
  patientNameInSentence,
  patientNameStartingSentence,
} from "@/frontend/utils/patient-name";
import {
  useMagicLinkActivityQuery,
  usePatientMagicLinkQuery,
  usePatientRecommendedSourcesQuery,
  usePatientSignatureQuery,
} from "@/frontend/queries/dashboard/patients";
import {
  useAddUserRecommendedSourceMutation,
  useDeletePatientSignatureMutation,
  useGenerateMagicLinkMutation,
  useRemoveUserRecommendedSourceMutation,
  useSetAskConsentSignatureMutation,
  useSetShowMobileAppMutation,
} from "@/frontend/mutations/dashboard/patients";
import {
  HealthSystemChip,
  HealthSystemOptionRow,
  HealthSystemSearch,
} from "./HealthSystemSearch";

/**
 * Shared column template for the link-history table. One class string used by
 * the header row and every body row, so the two can never size differently.
 */
const HISTORY_COLUMNS =
  "grid grid-cols-[minmax(0,1fr)_7rem_9.5rem_9.5rem] gap-3 px-3 py-2";

/**
 * One numbered step in the setup flow. `tip` becomes the `(i)` beside the
 * heading. `namesPatient` marks the title, hint and tip as PHI for session
 * replay, for the steps whose copy addresses the patient by name.
 */
function Step({
  index,
  title,
  tip,
  hint,
  status,
  namesPatient,
  children,
}: {
  index: number;
  title: string;
  tip: string;
  hint?: string;
  status?: ReactNode;
  namesPatient?: boolean;
  children: ReactNode;
}) {
  const patientAttr = namesPatient ? "" : undefined;
  return (
    <section className="flex gap-3.5 px-4 py-4">
      <span className="mt-px flex size-7 shrink-0 items-center justify-center rounded-full border bg-muted/40 text-caption font-semibold text-muted-foreground">
        {index}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <h2 className="text-meta font-semibold uppercase tracking-wide text-foreground" data-patient={patientAttr}>
            {title}
          </h2>
          <InfoHint label={tip} namesPatient={namesPatient} />
          <div className="flex-1" />
          {status}
        </div>
        {hint && (
          <p className="mt-0.5 text-caption text-muted-foreground" data-patient={patientAttr}>
            {hint}
          </p>
        )}
        <div className="mt-3">{children}</div>
      </div>
    </section>
  );
}

/**
 * The three things to set up before a patient connects, as one numbered flow.
 * Which health systems to put in front of them, what else to ask, and the link
 * itself. A null `patientFirstName` means no name is on file, and the copy then
 * says "this patient" rather than naming anyone.
 */
export function SetupFlow({
  patientMembershipId,
  patientFirstName,
  askConsentSignature,
  showMobileApp,
  canManage,
}: {
  patientMembershipId: string;
  patientFirstName: string | null;
  askConsentSignature: boolean;
  showMobileApp: boolean;
  canManage: boolean;
}) {
  const patient = patientNameInSentence(patientFirstName);
  const patientStartingSentence = patientNameStartingSentence(patientFirstName);
  const [copied, setCopied] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [freshUrl, setFreshUrl] = useState<string | null>(null);

  const { data: sources, isLoading: sourcesLoading } =
    usePatientRecommendedSourcesQuery(patientMembershipId);
  const { data: signature } = usePatientSignatureQuery(patientMembershipId);
  const { data: existingLink, isLoading: linkLoading } =
    usePatientMagicLinkQuery(patientMembershipId);

  const addSource = useAddUserRecommendedSourceMutation();
  const removeSource = useRemoveUserRecommendedSourceMutation();
  const setConsent = useSetAskConsentSignatureMutation();
  const setMobileApp = useSetShowMobileAppMutation();
  const deleteSignature = useDeletePatientSignatureMutation();
  const generateLink = useGenerateMagicLinkMutation((data) => setFreshUrl(data.magicLinkUrl));

  const saved = sources ?? [];
  const savedUrls = new Set(saved.map((source) => source.fhirBaseUrl));
  const wantsDefaults = canManage && !sourcesLoading && saved.length < SUGGESTED_SOURCE_TARGET;

  const { data: defaults } = useQuery({
    ...orpc.admin.getDefaultSuggestedSources.queryOptions({ input: {} }),
    enabled: wantsDefaults,
  });
  const unaddedDefaults = wantsDefaults
    ? (defaults?.sources ?? []).filter((source) => !savedUrls.has(source.fhirBaseUrl))
    : [];

  const url = freshUrl ?? existingLink?.magicLinkUrl ?? null;
  const remaining = existingLink?.expiresAt ? daysLeft(existingLink.expiresAt) : null;
  const expired = remaining === 0;

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const asks = onboardingAsks({
    patientStartingSentence,
    patientNamed: patientFirstName !== null,
    askConsentSignature,
    showMobileApp,
    consentPending: setConsent.isPending,
    mobileAppPending: setMobileApp.isPending,
    onAskConsentSignature: (checked) =>
      setConsent.mutate({ patientMembershipId, askConsentSignature: checked }),
    onShowMobileApp: (checked) =>
      setMobileApp.mutate({ patientMembershipId, showMobileApp: checked }),
  });

  return (
    <div className="divide-y rounded-lg border">
      <Step
        index={1}
        title="Suggest health systems"
        tip="These systems appear at the top of the patient's link so they can connect in one tap. They can always search for any other provider."
        hint={`Shown first when ${patient} opens their link. They can still search for anyone else.`}
        namesPatient={patientFirstName !== null}
        status={
          saved.length > 0 ? (
            <span className="text-meta tabular-nums text-muted-foreground">
              {saved.length} suggested
            </span>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-2.5">
          {sourcesLoading ? (
            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
          ) : (
            saved.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {saved.map((source) => (
                  <HealthSystemChip
                    key={source.id}
                    name={source.name}
                    logo={source.logo}
                    disabled={!canManage || removeSource.isPending}
                    onRemove={() => removeSource.mutate({ id: source.id })}
                  />
                ))}
              </div>
            )
          )}
          {canManage && (
            <HealthSystemSearch
              addedUrls={savedUrls}
              onSelect={(source) =>
                addSource.mutate({ patientMembershipId, fhirBaseUrl: source.fhirBaseUrl })
              }
            />
          )}
          {unaddedDefaults.length > 0 && (
            <div>
              <p className="mb-1.5 text-meta font-semibold uppercase tracking-wide text-foreground">
                Popular choices
              </p>
              <div className="divide-y overflow-hidden rounded-lg border">
                {unaddedDefaults.map((source) => (
                  <HealthSystemOptionRow
                    key={source.fhirBaseUrl}
                    source={source}
                    added={false}
                    onSelect={() =>
                      addSource.mutate({ patientMembershipId, fhirBaseUrl: source.fhirBaseUrl })
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </Step>

      <Step
        index={2}
        title={`What ${patient} is asked for`}
        tip={`Two optional asks inside the link: a signature ${patient} draws before connecting anything, and an offer to install the mobile app once they have.`}
        namesPatient={patientFirstName !== null}
      >
        <div className="flex flex-col gap-2">
          {asks.map((ask) => (
            <label
              key={ask.title}
              className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-body font-medium">{ask.title}</span>
                <span
                  className="mt-0.5 block text-caption text-muted-foreground"
                  data-patient={ask.namesPatient ? "" : undefined}
                >
                  {ask.body}
                </span>
              </span>
              <Switch
                checked={ask.checked}
                disabled={!canManage || ask.pending}
                onCheckedChange={ask.onChange}
              />
            </label>
          ))}

          {signature?.signatureImage && (
            <div className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 overflow-hidden rounded border bg-white p-1 transition-opacity hover:opacity-80"
                  >
                    <img
                      src={signature.signatureImage}
                      alt="Captured consent signature"
                      className="h-7 w-16 object-contain"
                      data-ph-no-capture
                    />
                  </button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-body">Consent signature</DialogTitle>
                  </DialogHeader>
                  <img
                    src={signature.signatureImage}
                    alt="Captured consent signature"
                    className="w-full rounded border bg-white p-4"
                    data-ph-no-capture
                  />
                </DialogContent>
              </Dialog>
              <span className="min-w-0 flex-1">
                <span className="block text-body font-medium">Already signed</span>
                <span className="mt-0.5 block text-caption text-muted-foreground">
                  Deleting the signature asks <span data-patient>{patient}</span> again.
                </span>
              </span>
              {canManage && (
                <Button
                  variant="outline"
                  className="h-8 shrink-0 text-caption text-destructive hover:text-destructive"
                  disabled={deleteSignature.isPending}
                  onClick={() => deleteSignature.mutate({ patientMembershipId })}
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </Step>

      <Step
        index={3}
        title="Share their link"
        tip={`The magic link signs ${patient} in without a password. Share it over any channel you already use.`}
        hint={`Send it however you already talk to ${patient}. Medblocks never sends it for you.`}
        namesPatient={patientFirstName !== null}
        status={
          url && remaining !== null ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-caption font-medium",
                expired
                  ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                  : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
              )}
            >
              <span
                className={cn("size-1.5 rounded-full", expired ? "bg-amber-500" : "bg-emerald-500")}
              />
              {expired ? "Expired" : `Active · ${remaining}d left`}
            </span>
          ) : undefined
        }
      >
        {linkLoading ? (
          <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
        ) : (
          <div className="flex flex-col gap-2.5">
            {url && (
              <div className="flex items-center gap-2">
                <code
                  className="min-w-0 flex-1 truncate rounded-lg border bg-muted/40 px-3 py-2 font-mono text-caption"
                  data-ph-no-capture
                >
                  {url}
                </code>
                <Button className="h-9 shrink-0 gap-1.5 text-body" onClick={copy}>
                  {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={url ? "outline" : "default"}
                className="h-8 gap-1.5 text-caption"
                disabled={generateLink.isPending || !canManage}
                onClick={() => {
                  setFreshUrl(null);
                  generateLink.mutate({ patientMembershipId });
                }}
              >
                {generateLink.isPending ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Send className="size-3" />
                )}
                {url ? "Generate a new link" : "Generate magic link"}
              </Button>
              <Button
                variant="outline"
                className="h-8 gap-1.5 text-caption"
                onClick={() => setHistoryOpen((open) => !open)}
              >
                <History className="size-3" />
                {historyOpen ? "Hide history" : "History"}
              </Button>
            </div>

            {historyOpen && <MagicLinkHistory patientMembershipId={patientMembershipId} />}
          </div>
        )}
      </Step>
    </div>
  );
}

/**
 * Link history, rendered inline under step 3 rather than in a dialog that would
 * cover the preview it is read alongside. Header and body rows share
 * `HISTORY_COLUMNS` so the headings stay over the right cells. Exported so a
 * test can render it without the toggle.
 */
export function MagicLinkHistory({ patientMembershipId }: { patientMembershipId: string }) {
  const { data, isLoading } = useMagicLinkActivityQuery(patientMembershipId);
  const rows = data?.rows ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border px-3 py-3">
        <Loader2 className="size-3 animate-spin text-muted-foreground" />
        <span className="text-caption text-muted-foreground">Loading history…</span>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="rounded-lg border px-3 py-3 text-caption text-muted-foreground">
        No links generated yet.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <div
        className={cn(
          HISTORY_COLUMNS,
          "border-b bg-muted/40 text-caption font-medium text-muted-foreground",
        )}
      >
        <span className="truncate">Generated by</span>
        <span className="truncate">Status</span>
        <span className="truncate">Created at</span>
        <span className="truncate">Updated at</span>
      </div>
      <div className="divide-y">
        {rows.map((row) => (
          <div key={row.id} className={cn(HISTORY_COLUMNS, "text-caption")}>
            <span className="truncate">{row.createdBy ?? "Unknown"}</span>
            <span
              className={cn(
                "truncate font-medium",
                row.accessedAt
                  ? "text-emerald-600 dark:text-emerald-400"
                  : row.expired
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-muted-foreground",
              )}
            >
              {row.accessedAt ? "Opened" : row.expired ? "Expired unused" : "Not opened"}
            </span>
            <span className="truncate text-muted-foreground">
              {row.createdAt ? formatDateTime(row.createdAt) : "—"}
            </span>
            {/* When the link was last acted on, which for a link means opened. */}
            <span className="truncate text-muted-foreground">
              {row.accessedAt ? formatDateTime(row.accessedAt) : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
